import { jsPDF } from 'jspdf';
import { formatDate, formatPhone, formatCpfCnpj } from './formatters';

/**
 * Gera um relatório profissional de propriedades rurais em PDF
 */
export const gerarRelatorioSimples = (propriedades, residencias, fossas, filtros = {}) => {
  // Criar documento PDF
  const doc = new jsPDF();
  
  // Variáveis para totalizadores
  let totalResidencias = 0;
  let totalMoradores = 0;
  let totalFossas = 0;
  let totalFossasConvencionais = 0;
  let totalFossasBiodigestoras = 0;
  let totalFossasEcologicas = 0;
  let totalFossasSumidouros = 0;
  
  // Cores e estilos
  const corPrimaria = [50, 50, 50]; // Cinza escuro
  const corSecundaria = [100, 100, 100]; // Cinza médio
  const corDestaque = [46, 125, 50]; // Verde institucional
  const corFundo = [240, 240, 240]; // Cinza claro
  
  // Adicionar cabeçalho
  doc.setFillColor(...corDestaque);
  doc.rect(0, 0, 210, 25, 'F');
  
  doc.setFontSize(18);
  doc.setTextColor(255);
  doc.text('Relatório de Propriedades Rurais', 105, 15, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setTextColor(230);
  doc.text(`Prefeitura Teste`, 105, 22, { align: 'center' });
  
  // Adicionar data e filtros
  doc.setTextColor(...corPrimaria);
  doc.setFontSize(9);
  doc.text(`Gerado em: ${formatDate(new Date())}`, 15, 32);
  
  // Adicionar filtros aplicados, se houver
  if (Object.keys(filtros).length > 0) {
    let filtroTexto = 'Filtros aplicados: ';
    
    if (filtros.localidade) {
      filtroTexto += `Localidade: ${filtros.localidade}, `;
    }
    
    if (filtros.status) {
      filtroTexto += `Status: ${filtros.status}, `;
    }
    
    if (filtros.agente) {
      filtroTexto += `Agente: ${filtros.agente}, `;
    }
    
    // Remover a última vírgula e espaço
    filtroTexto = filtroTexto.replace(/, $/, '');
    
    doc.text(filtroTexto, 15, 37);
  }
  
  // Linha separadora
  doc.setDrawColor(...corDestaque);
  doc.setLineWidth(0.5);
  doc.line(15, 40, 195, 40);
  
  // Posição Y atual no documento
  let yPos = 50;
  
  // Agrupar propriedades por localidade
  const propriedadesPorLocalidade = {};
  
  propriedades.forEach(prop => {
    const localidadeId = prop.localidade_id || 'sem-localidade';
    const localidadeNome = prop.localidade?.nome || 'Sem Localidade Definida';
    
    if (!propriedadesPorLocalidade[localidadeId]) {
      propriedadesPorLocalidade[localidadeId] = {
        nome: localidadeNome,
        propriedades: []
      };
    }
    
    propriedadesPorLocalidade[localidadeId].propriedades.push(prop);
  });
  
  // Para cada localidade
  Object.entries(propriedadesPorLocalidade).forEach(([localidadeId, localidade]) => {
    // Verificar se precisa adicionar nova página
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    
    // Título da localidade
    doc.setFillColor(...corFundo);
    doc.rect(15, yPos - 5, 180, 10, 'F');
    doc.setDrawColor(...corDestaque);
    doc.setLineWidth(0.5);
    doc.line(15, yPos - 5, 15, yPos + 5);
    
    doc.setFontSize(12);
    doc.setTextColor(...corDestaque);
    doc.text(`${localidade.nome} (${localidade.propriedades.length} propriedades)`, 20, yPos);
    yPos += 10;
    
    // Para cada propriedade
    localidade.propriedades.forEach((prop, index) => {
      // Verificar se precisa adicionar nova página
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      // Cabeçalho da propriedade
      doc.setFillColor(...corFundo);
      doc.rect(15, yPos - 4, 180, 8, 'F');
      doc.setFontSize(10);
      doc.setTextColor(...corPrimaria);
      doc.setFont('helvetica', 'bold');
      doc.text(`${index + 1}. ${prop.nome}`, 20, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 8;
      
      // Informações da propriedade
      doc.setFontSize(9);
      doc.setTextColor(...corSecundaria);
      
      // Primeira linha de informações
      doc.text(`Proprietário: ${prop.nome_proprietario}`, 20, yPos);
      doc.text(`Status: ${prop.status}`, 110, yPos);
      yPos += 5;
      
      // Segunda linha de informações
      doc.text(`Contato: ${formatPhone(prop.telefone)}`, 20, yPos);
      doc.text(`CPF/CNPJ: ${prop.cpf_cnpj ? formatCpfCnpj(prop.cpf_cnpj) : 'Não informado'}`, 110, yPos);
      yPos += 5;
      
      // Terceira linha de informações
      doc.text(`Agente: ${prop.agente?.nome || 'Não atribuído'}`, 20, yPos);
      doc.text(`Coordenadas: ${prop.latitude.toFixed(6)}, ${prop.longitude.toFixed(6)}`, 110, yPos);
      yPos += 8;
      
      // Residências da propriedade
      const residenciasDaPropriedade = residencias.filter(r => r.propriedade_id === prop.id);
      totalResidencias += residenciasDaPropriedade.length;
      
      if (residenciasDaPropriedade.length > 0) {
        // Cabeçalho da tabela de residências
        doc.setFillColor(230, 230, 230);
        doc.rect(20, yPos - 4, 170, 7, 'F');
        doc.setTextColor(...corPrimaria);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('Residência', 22, yPos);
        doc.text('Status', 62, yPos);
        doc.text('Moradores', 87, yPos);
        doc.text('Coordenadas', 112, yPos);
        doc.text('Fossas', 177, yPos);
        doc.setFont('helvetica', 'normal');
        yPos += 5;
        
        // Dados das residências
        doc.setTextColor(...corSecundaria);
        
        residenciasDaPropriedade.forEach((residencia, resIndex) => {
          // Contar moradores
          totalMoradores += residencia.numero_moradores;
          
          // Fossas da residência
          const fossasDaResidencia = fossas.filter(f => f.residencia_id === residencia.id);
          totalFossas += fossasDaResidencia.length;
          
          // Alternar cor de fundo
          if (resIndex % 2 === 0) {
            doc.setFillColor(245, 245, 245);
            doc.rect(20, yPos - 3, 170, 6, 'F');
          }
          
          // Dados da residência
          doc.setFontSize(8);
          doc.text(residencia.identificacao, 22, yPos);
          doc.text(residencia.status, 62, yPos);
          doc.text(residencia.numero_moradores.toString(), 87, yPos);
          doc.text(`${residencia.latitude.toFixed(6)}, ${residencia.longitude.toFixed(6)}`, 112, yPos);
          doc.text(fossasDaResidencia.length.toString(), 177, yPos);
          
          yPos += 6;
          
          // Verificar se precisa adicionar nova página
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          
          // Se tem fossas, listar
          if (fossasDaResidencia.length > 0) {
            // Cabeçalho da tabela de fossas
            doc.setFillColor(245, 245, 245);
            doc.rect(25, yPos - 3, 160, 6, 'F');
            doc.setTextColor(...corPrimaria);
            doc.setFontSize(7);
            doc.setFont('helvetica', 'bold');
            doc.text('Tipo', 27, yPos);
            doc.text('Data Instalação', 57, yPos);
            doc.text('Capacidade', 87, yPos);
            doc.text('Coordenadas', 117, yPos);
            doc.setFont('helvetica', 'normal');
            yPos += 5;
            
            // Dados das fossas
            doc.setTextColor(...corSecundaria);
            fossasDaResidencia.forEach(fossa => {
              // Contabilizar por tipo
              if (fossa.tipo === 'Convencional') totalFossasConvencionais++;
              else if (fossa.tipo === 'Biodigestora') totalFossasBiodigestoras++;
              else if (fossa.tipo === 'Ecologica') totalFossasEcologicas++;
              else if (fossa.tipo === 'Sumidouro') totalFossasSumidouros++;
              
              doc.setFontSize(7);
              doc.text(fossa.tipo, 27, yPos);
              doc.text(fossa.data_instalacao ? formatDate(fossa.data_instalacao) : 'N/A', 57, yPos);
              doc.text(fossa.capacidade || 'N/A', 87, yPos);
              doc.text(`${fossa.latitude.toFixed(6)}, ${fossa.longitude.toFixed(6)}`, 117, yPos);
              
              yPos += 5;
              
              // Verificar se precisa adicionar nova página
              if (yPos > 270) {
                doc.addPage();
                yPos = 20;
              }
            });
            
            yPos += 2;
          }
        });
      } else {
        doc.setFontSize(8);
        doc.setTextColor(...corSecundaria);
        doc.text('Sem residências cadastradas', 25, yPos);
        yPos += 5;
      }
      
      // Linha separadora entre propriedades
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.2);
      doc.line(20, yPos, 190, yPos);
      yPos += 10;
    });
    
    yPos += 5;
  });
  
  // Adicionar página para totalizadores
  doc.addPage();
  
  // Cabeçalho da página de totalizadores
  doc.setFillColor(...corDestaque);
  doc.rect(0, 0, 210, 25, 'F');
  
  doc.setFontSize(18);
  doc.setTextColor(255);
  doc.text('Resumo Geral', 105, 15, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setTextColor(230);
  doc.text(`Prefeitura Teste`, 105, 22, { align: 'center' });
  
  // Totalizadores
  yPos = 40;
  
  // Tabela de totalizadores
  doc.setFillColor(245, 245, 245);
  doc.rect(40, yPos - 5, 120, 10, 'F');
  doc.setFontSize(10);
  doc.setTextColor(...corPrimaria);
  doc.setFont('helvetica', 'bold');
  doc.text('Indicador', 45, yPos);
  doc.text('Valor', 145, yPos, { align: 'center' });
  yPos += 10;
  
  // Dados dos totalizadores
  const totalizadores = [
    ['Total de Propriedades', propriedades.length.toString()],
    ['Total de Residências', totalResidencias.toString()],
    ['Total de Moradores', totalMoradores.toString()],
    ['Média de Moradores por Residência', totalResidencias > 0 ? (totalMoradores / totalResidencias).toFixed(1) : '0'],
    ['Total de Fossas Sépticas', totalFossas.toString()],
    ['Fossas Convencionais', totalFossasConvencionais.toString()],
    ['Fossas Biodigestoras', totalFossasBiodigestoras.toString()],
    ['Fossas Ecológicas', totalFossasEcologicas.toString()],
    ['Sumidouros', totalFossasSumidouros.toString()],
    ['Residências com Fossa', (new Set(fossas.map(f => f.residencia_id))).size.toString()],
    ['Residências sem Fossa', (totalResidencias - (new Set(fossas.map(f => f.residencia_id))).size).toString()],
    ['Média de Fossas por Residência', totalResidencias > 0 ? (totalFossas / totalResidencias).toFixed(1) : '0']
  ];
  
  // Desenhar tabela de totalizadores
  doc.setFontSize(9);
  totalizadores.forEach((row, index) => {
    // Alternar cor de fundo
    if (index % 2 === 0) {
      doc.setFillColor(245, 245, 245);
      doc.rect(40, yPos - 5, 120, 8, 'F');
    }
    
    // Título em negrito
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...corPrimaria);
    doc.text(row[0], 45, yPos);
    
    // Valor
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...corSecundaria);
    doc.text(row[1], 145, yPos, { align: 'center' });
    
    yPos += 8;
  });
  
  // Rodapé
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Linha do rodapé
    doc.setDrawColor(...corDestaque);
    doc.setLineWidth(0.5);
    doc.line(15, 280, 195, 280);
    
    // Texto do rodapé
    doc.setFontSize(8);
    doc.setTextColor(...corSecundaria);
    doc.text(`Página ${i} de ${pageCount}`, 105, 287, { align: 'center' });
    doc.text('Sistema de Administração de Fossas Sépticas', 105, 292, { align: 'center' });
  }
  
  // Salvar o PDF
  doc.save('relatorio_propriedades_rurais.pdf');
};

export default {
  gerarRelatorioSimples
};